import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import crmService from "../../crmConfig.json";
import { crmAxios } from "../../app/crmApi";
import { errorToast, successToast } from "../../helpers/toaster";

const initialState = {
    staff: [],
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    permissionCatalog: [],
    loading: false,
    saving: false,
};

export const fetchStaffAsync = createAsyncThunk(
    "crmStaff/fetchStaff",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.adminStaff, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchPermissionCatalogAsync = createAsyncThunk(
    "crmStaff/fetchPermissionCatalog",
    async (_, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.adminStaffPermissions);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const createStaffAsync = createAsyncThunk(
    "crmStaff/createStaff",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await crmAxios.post(crmService.adminStaff, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const updateStaffAsync = createAsyncThunk(
    "crmStaff/updateStaff",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.put(`${crmService.adminStaff}/${id}`, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const setStaffPermissionsAsync = createAsyncThunk(
    "crmStaff/setPermissions",
    async ({ id, permissionKeys }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.put(
                `${crmService.adminStaff}/${id}/permissions`,
                { permissionKeys }
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

const staffAction = (name, method, suffix) =>
    createAsyncThunk(name, async ({ id, ...body }, { rejectWithValue }) => {
        try {
            const url = `${crmService.adminStaff}/${id}${suffix}`;
            const res = await crmAxios[method](url, body);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    });

export const activateStaffAsync = staffAction(
    "crmStaff/activateStaff",
    "patch",
    "/activate"
);
export const deactivateStaffAsync = staffAction(
    "crmStaff/deactivateStaff",
    "patch",
    "/deactivate"
);
export const resetStaffPasswordAsync = staffAction(
    "crmStaff/resetStaffPassword",
    "post",
    "/reset-password"
);

const applyStaffUpdate = (state, payload) => {
    if (!payload?.data?.id) return;
    state.staff = state.staff.map((s) => (s.id === payload.data.id ? payload.data : s));
};

const isActionThunk = (thunks, phase) => (action) =>
    thunks.some((thunk) => action.type === thunk[phase].type);

const mutatingThunks = [
    setStaffPermissionsAsync,
    activateStaffAsync,
    deactivateStaffAsync,
    resetStaffPasswordAsync,
];
const staffUpdatingThunks = [
    setStaffPermissionsAsync,
    activateStaffAsync,
    deactivateStaffAsync,
];

const crmStaffSlice = createSlice({
    name: "crmStaff",
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(fetchStaffAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchStaffAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.staff = payload.data.staff;
                    state.pagination = payload.data.pagination;
                }
            })
            .addCase(fetchStaffAsync.rejected, (state, { payload }) => {
                state.loading = false;
                errorToast(payload?.message || "Failed to load staff");
            })
            .addCase(fetchPermissionCatalogAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.permissionCatalog = payload.data;
            })
            .addCase(createStaffAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(createStaffAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.staff = [payload.data, ...state.staff];
                } else {
                    errorToast(payload?.message || "Failed to create staff");
                }
            })
            .addCase(createStaffAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to create staff");
            })
            .addCase(updateStaffAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(updateStaffAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    applyStaffUpdate(state, payload);
                } else {
                    errorToast(payload?.message || "Failed to update staff");
                }
            })
            .addCase(updateStaffAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to update staff");
            })
            .addCase(resetStaffPasswordAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) successToast(payload.message);
                else errorToast(payload?.message || "Failed to reset password");
            })
            .addMatcher(isActionThunk(mutatingThunks, "pending"), (state) => {
                state.saving = true;
            })
            .addMatcher(
                isActionThunk(staffUpdatingThunks, "fulfilled"),
                (state, { payload }) => {
                    state.saving = false;
                    if (payload?.success) {
                        successToast(payload.message);
                        applyStaffUpdate(state, payload);
                    } else {
                        errorToast(payload?.message || "Action failed");
                    }
                }
            )
            .addMatcher(isActionThunk(mutatingThunks, "rejected"), (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Action failed");
            });
    },
});

export const selectStaff = (state) => state.crmStaff.staff;
export const selectStaffPagination = (state) => state.crmStaff.pagination;
export const selectStaffLoading = (state) => state.crmStaff.loading;
export const selectStaffSaving = (state) => state.crmStaff.saving;
export const selectPermissionCatalog = (state) => state.crmStaff.permissionCatalog;

export default crmStaffSlice.reducer;
